import { Sheet, SheetContent, SheetClose } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { X, Plus, Minus } from 'lucide-react';
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface MenuItem {
  id: string;
  name: string;
  description: string;
  price: number;
  image_url: string;
  is_customizable: boolean;
  original_price?: number;
}

interface CustomizationOption {
  id: string;
  name: string;
  price_type: string;
  price_amount: number;
}

interface Customization {
  id: string;
  name: string;
  type: string;
  is_required: boolean;
  options: CustomizationOption[];
}

interface SelectedCustomization {
  customization_id: string;
  selected_options: string[];
  text_input?: string;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  item: MenuItem | null;
  displayPrice?: number;
  originalPrice?: number;
  canOrder: boolean;
  onAddToCart: (item: MenuItem, quantity: number, customizations?: SelectedCustomization[], finalUnitPrice?: number) => void;
}

export const MenuItemDetailsDialog = ({
  isOpen,
  onClose,
  item,
  displayPrice,
  originalPrice,
  canOrder,
  onAddToCart,
}: Props) => {
  const [quantity, setQuantity] = useState(1);
  const [customizations, setCustomizations] = useState<Customization[]>([]);
  const [selections, setSelections] = useState<SelectedCustomization[]>([]);
  const [loadingCustomizations, setLoadingCustomizations] = useState(false);

  useEffect(() => {
    if (!isOpen || !item) return;
    setQuantity(1);
    setCustomizations([]);
    setSelections([]);
    if (item.is_customizable) {
      fetchCustomizations(item.id);
    }
  }, [isOpen, item?.id]);

  const fetchCustomizations = async (menuItemId: string) => {
    setLoadingCustomizations(true);
    try {
      const { data, error } = await supabase
        .from('menu_item_customizations')
        .select(`
          id, name, type, is_required,
          customization_options ( id, name, price_type, price_amount )
        `)
        .eq('menu_item_id', menuItemId);
      if (error) throw error;
      const formatted = (data || []).map(c => ({
        id: c.id,
        name: c.name,
        type: c.type,
        is_required: c.is_required,
        options: c.customization_options || [],
      }));
      setCustomizations(formatted);
      setSelections(formatted.map(c => ({ customization_id: c.id, selected_options: [], text_input: '' })));
    } catch (e) {
      console.error(e);
      toast.error('Failed to load options');
    } finally {
      setLoadingCustomizations(false);
    }
  };

  if (!item) return null;

  const basePrice = displayPrice ?? item.price;

  const updateSelection = (cid: string, optionId?: string, text?: string) => {
    setSelections(prev => prev.map(sc => {
      if (sc.customization_id !== cid) return sc;
      const c = customizations.find(x => x.id === cid);
      if (!c) return sc;
      if (c.type === 'text_input') return { ...sc, text_input: text };
      if (c.type === 'single_select' && optionId) return { ...sc, selected_options: [optionId] };
      if (c.type === 'multiple_select' && optionId) {
        const has = sc.selected_options.includes(optionId);
        return { ...sc, selected_options: has ? sc.selected_options.filter(i => i !== optionId) : [...sc.selected_options, optionId] };
      }
      return sc;
    }));
  };

  const unitPriceWithCustom = () => {
    let total = basePrice;
    selections.forEach(sc => {
      const c = customizations.find(x => x.id === sc.customization_id);
      if (!c) return;
      sc.selected_options.forEach(oid => {
        const o = c.options.find(op => op.id === oid);
        if (o && o.price_type === 'fixed') total += o.price_amount;
      });
    });
    return total;
  };

  const totalPrice = unitPriceWithCustom() * quantity;

  const validate = () => {
    for (const c of customizations) {
      if (!c.is_required) continue;
      const sel = selections.find(s => s.customization_id === c.id);
      if (c.type === 'text_input') {
        if (!sel?.text_input?.trim()) { toast.error(`${c.name} is required`); return false; }
      } else if (!sel?.selected_options?.length) {
        toast.error(`Please select an option for ${c.name}`); return false;
      }
    }
    return true;
  };

  const handleAdd = () => {
    if (!canOrder) return;
    if (item.is_customizable && !validate()) return;
    onAddToCart(item, quantity, item.is_customizable ? selections : undefined, item.is_customizable ? unitPriceWithCustom() : undefined);
    onClose();
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent side="bottom" className="p-0 h-[92vh] max-h-[92vh] rounded-t-3xl flex flex-col">
        <SheetClose className="absolute right-4 top-4 z-20 rounded-full bg-background/90 p-2 hover:bg-background shadow-md">
          <X className="h-5 w-5" />
        </SheetClose>

        <div className="flex-1 overflow-y-auto">
          {/* Universal image */}
          <div className="px-4 pt-4">
            <div className="w-full aspect-square bg-muted rounded-2xl overflow-hidden">
              {item.image_url ? (
                <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-6xl">🍽️</div>
              )}
            </div>
          </div>

          {/* Header info */}
          <div className="p-5 border-b border-border">
            <div className="flex items-start justify-between gap-3 mb-2">
              <h2 className="text-2xl font-bold text-foreground">{item.name}</h2>
              <div className="flex flex-col items-end">
                {originalPrice && originalPrice !== basePrice && (
                  <span className="text-sm text-muted-foreground line-through">D{originalPrice}</span>
                )}
                <span className="text-xl font-bold text-red-600">D{basePrice}</span>
              </div>
            </div>
            {item.description && (
              <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line">
                {item.description}
              </p>
            )}
          </div>

          {/* Customizations */}
          {item.is_customizable && (
            <div className="divide-y divide-border">
              {loadingCustomizations ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-4 border-red-600 border-t-transparent" />
                </div>
              ) : customizations.length === 0 ? (
                <p className="p-5 text-sm text-muted-foreground">No customization options available.</p>
              ) : (
                customizations.map(c => (
                  <div key={c.id} className="p-5">
                    <div className="mb-3">
                      <h3 className="text-lg font-bold text-foreground">{c.name}</h3>
                      <p className="text-xs text-muted-foreground">
                        {c.is_required
                          ? c.type === 'multiple_select' ? 'Choose at least 1' : 'Choose 1'
                          : 'Optional'}
                      </p>
                    </div>

                    {c.type === 'text_input' ? (
                      <Input
                        placeholder={`Enter ${c.name.toLowerCase()}`}
                        value={selections.find(s => s.customization_id === c.id)?.text_input || ''}
                        onChange={e => updateSelection(c.id, undefined, e.target.value)}
                      />
                    ) : c.type === 'single_select' ? (
                      <RadioGroup
                        value={selections.find(s => s.customization_id === c.id)?.selected_options[0] || ''}
                        onValueChange={v => updateSelection(c.id, v)}
                        className="space-y-1"
                      >
                        {c.options.map(o => (
                          <Label
                            key={o.id}
                            htmlFor={o.id}
                            className="flex items-center justify-between py-2 cursor-pointer"
                          >
                            <span className="text-base text-foreground">{o.name}</span>
                            <div className="flex items-center gap-3">
                              {o.price_type === 'fixed' && o.price_amount > 0 && (
                                <span className="text-base font-semibold text-foreground">+D{o.price_amount}</span>
                              )}
                              <RadioGroupItem value={o.id} id={o.id} className="border-red-600 text-red-600" />
                            </div>
                          </Label>
                        ))}
                      </RadioGroup>
                    ) : c.type === 'multiple_select' ? (
                      <div className="space-y-1">
                        {c.options.map(o => {
                          const sel = selections.find(s => s.customization_id === c.id);
                          const checked = sel?.selected_options.includes(o.id) || false;
                          return (
                            <Label
                              key={o.id}
                              htmlFor={o.id}
                              className="flex items-center justify-between py-2 cursor-pointer"
                            >
                              <span className="text-base text-foreground">{o.name}</span>
                              <div className="flex items-center gap-3">
                                {o.price_type === 'fixed' && o.price_amount > 0 && (
                                  <span className="text-base font-semibold text-foreground">+D{o.price_amount}</span>
                                )}
                                <Checkbox
                                  id={o.id}
                                  checked={checked}
                                  onCheckedChange={() => updateSelection(c.id, o.id)}
                                  className="border-red-600 data-[state=checked]:bg-red-600 data-[state=checked]:border-red-600"
                                />
                              </div>
                            </Label>
                          );
                        })}
                      </div>
                    ) : null}
                  </div>
                ))
              )}
            </div>
          )}

          {/* Spacer for sticky footer */}
          <div className="h-24" />
        </div>

        {/* Sticky footer */}
        <div className="border-t border-border bg-background p-4 flex items-center gap-3">
          <div className="flex items-center gap-1 border-2 border-red-600 rounded-full px-2 py-1">
            <Button
              size="icon"
              variant="ghost"
              className="h-8 w-8 rounded-full text-red-600 hover:bg-red-50"
              onClick={() => setQuantity(q => Math.max(1, q - 1))}
              disabled={quantity <= 1}
            >
              <Minus className="h-4 w-4" />
            </Button>
            <span className="font-bold min-w-[1.5rem] text-center text-foreground">{quantity}</span>
            <Button
              size="icon"
              variant="ghost"
              className="h-8 w-8 rounded-full text-red-600 hover:bg-red-50"
              onClick={() => setQuantity(q => q + 1)}
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
          <Button
            className={`flex-1 h-12 text-base font-semibold rounded-full ${
              canOrder ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
            onClick={handleAdd}
            disabled={!canOrder}
          >
            Add to cart{canOrder ? ` · D${totalPrice}` : ''}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};
